import { useAdvisories } from '@/features/advisory/hooks';
import { brokersService } from '@/services/brokers';
import type { Broker, BrokerApiResponse } from '@/services/brokers/types';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useDebouncedValue } from './use-filters.hook';

export interface OptionItem {
  label: string;
  value: string;
}

/** Página pequena usada só para resolver o nome de um código já selecionado. */
const LOOKUP_PAGE_SIZE = 5;

/**
 * `listBrokers` ora devolve `{ data: [...] }`, ora `{ data: { data: [...] } }`
 * (o tipo da service não bate com a resposta real da API). Normaliza os dois.
 */
async function fetchBrokerList(
  search: string,
  pageSize: number
): Promise<BrokerApiResponse[]> {
  const result = await brokersService.listBrokers({
    page: 1,
    pageSize,
    search,
  });

  if (!result.success || !result.data) {
    throw new Error(result.error || 'Erro ao carregar corretores');
  }

  const payload = result.data.data as unknown as
    | BrokerApiResponse[]
    | { data?: BrokerApiResponse[] }
    | undefined;

  if (Array.isArray(payload)) return payload;
  return payload?.data ?? [];
}

const toBrokerOption = (broker: BrokerApiResponse): OptionItem => ({
  label: broker.name,
  value: broker.partyId || broker.code,
});

/** O valor guardado no form pode ser o partyId, o partyCode ou o id numérico. */
const isSameBroker = (broker: BrokerApiResponse, value: string) =>
  broker.partyId === value ||
  broker.code === value ||
  String(broker.id) === value;

export function useAdvisoryOptions({
  search,
  pageSize = 10,
  partyType,
  brokerId,
  productCode,
  enabled = true,
}: {
  search: string;
  pageSize?: number;
  partyType?: 'advisory' | 'broker';
  brokerId?: string;
  productCode?: string;
  enabled?: boolean;
}) {
  const debouncedSearch = useDebouncedValue(search, 300);

  // Hook para assessorias (partyType === 'advisory')
  const { data: advisoryData, isLoading: isLoadingAdvisories } = useAdvisories({
    search: debouncedSearch,
    pageNo: 1,
    pageSize,
    partyType: '14',
    enabled: enabled && partyType === 'advisory',
  });

  // Query para corretores (partyType === 'broker')
  const useBrokerSearch =
    partyType === 'broker' && Boolean(brokerId) && Boolean(productCode);

  const { data: brokerSearchData, isLoading: isLoadingBrokerSearch } = useQuery<
    Broker[]
  >({
    queryKey: ['brokers', 'search', brokerId, productCode],
    queryFn: async () => {
      const result = await brokersService.searchBrokers({
        id: brokerId!,
        productCode: productCode!,
      });
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Erro ao buscar corretores');
      }
      return result.data;
    },
    enabled: enabled && useBrokerSearch,
  });

  const { data: brokerListData, isLoading: isLoadingBrokerList } = useQuery({
    queryKey: ['brokers', 'list', debouncedSearch, pageSize],
    queryFn: () => fetchBrokerList(debouncedSearch, pageSize),
    enabled: enabled && partyType === 'broker' && !useBrokerSearch,
  });

  const items: OptionItem[] = useMemo(() => {
    if (partyType === 'advisory') {
      return (advisoryData?.advisories || []).map(a => ({
        label: a.name,
        value: a.partyCode,
      }));
    }

    if (partyType === 'broker') {
      if (useBrokerSearch) {
        return (brokerSearchData ?? []).map(b => ({
          label: b.name,
          value: b.partyId || b.code || b.id,
        }));
      }

      // `fetchBrokerList` já devolve a lista extraída — o `.data` aqui era um
      // nível a mais.
      const brokers = brokerListData ?? [];
      return brokers.map(toBrokerOption);
    }

    return [];
  }, [
    partyType,
    advisoryData,
    brokerSearchData,
    brokerListData,
    useBrokerSearch,
  ]);

  const isLoading =
    partyType === 'advisory'
      ? isLoadingAdvisories
      : useBrokerSearch
        ? isLoadingBrokerSearch
        : isLoadingBrokerList;

  const brokerByValue = useMemo(() => {
    const map = new Map<
      string,
      { licenseNumber?: string; susepCode?: string }
    >();
    if (partyType !== 'broker') return map;
    const source = useBrokerSearch
      ? (brokerSearchData ?? [])
      : (brokerListData ?? []);
    for (const broker of source) {
      const value = broker.partyId || broker.code || broker.id;
      if (!value) continue;
      map.set(value, {
        licenseNumber: broker.licenseNumber,
        susepCode: broker.susepCode,
      });
    }
    return map;
  }, [partyType, useBrokerSearch, brokerSearchData, brokerListData]);

  return {
    items,
    isLoading,
    brokerByValue,
  } as const;
}

/**
 * Resolve o nome da assessoria/corretora a partir do código já selecionado,
 * consultando a API pelo próprio código (o `filter` da API também casa com
 * `PartyCode`).
 *
 * Necessário porque a lista do combobox é paginada e filtrada no servidor: em
 * produção há mais parceiros que o `pageSize`, então o item selecionado pode
 * não estar na página atual e o combobox acabaria exibindo o código cru
 * (ex.: `PTY10000205097655`) no lugar do nome.
 */
export function useAdvisoryOptionLabel({
  value,
  partyType,
}: {
  value?: string;
  partyType?: 'advisory' | 'broker';
}) {
  const enabled = Boolean(value) && Boolean(partyType);

  const { data: advisoryData, isFetching: isFetchingAdvisory } = useAdvisories({
    search: value,
    pageNo: 1,
    pageSize: LOOKUP_PAGE_SIZE,
    partyType: '14',
    enabled: enabled && partyType === 'advisory',
  });

  const { data: brokerData, isFetching: isFetchingBroker } = useQuery({
    queryKey: ['brokers', 'list', value, LOOKUP_PAGE_SIZE],
    queryFn: () => fetchBrokerList(value ?? '', LOOKUP_PAGE_SIZE),
    enabled: enabled && partyType === 'broker',
  });

  const label = useMemo(() => {
    if (!value || !enabled) return undefined;

    if (partyType === 'advisory') {
      return (advisoryData?.advisories ?? []).find(
        a => a.partyCode === value || String(a.id) === value
      )?.name;
    }

    return (brokerData ?? []).find(b => isSameBroker(b, value))?.name;
  }, [value, enabled, partyType, advisoryData, brokerData]);

  const isLoading =
    enabled &&
    (partyType === 'advisory' ? isFetchingAdvisory : isFetchingBroker);

  return { label, isLoading } as const;
}
